/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Injectable, inject, DestroyRef } from '@angular/core'
import { StorageService } from './storage.service'
import { WINDOW } from '../utils/window-token'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { HttpClient } from '@angular/common/http'
import { map, Observable, Subscription, timer, fromEvent } from 'rxjs'
import { environment } from '../../environments/environment'
import { Usuario } from '../models/usuario'
import { Router } from '@angular/router'
import { ToastService } from '../modules/toast/services/toast.service'
import { TranslateService } from '@ngx-translate/core'

@Injectable({
  providedIn: 'root',
})
export class UsuarioService {
  private http = inject(HttpClient)
  private router = inject(Router)
  private toastService = inject(ToastService)
  private readonly translateService = inject(TranslateService)
  private destroyRef = inject(DestroyRef)
  private storage = inject(StorageService)
  private window = inject(WINDOW)

  private baseUrl: string
  private refreshSub?: Subscription
  private static readonly REFRESH_MARGIN_MS = 5 * 60 * 1000 // 5 minutos antes de expirar

  i18n = {
    atencao: '',
    sejaBemVindo: '',
    identificadoComSucesso: '',
    emailOuSenhaInvalido: '',
    acessoFinalizado: '',
    sessaoExpirada: '',
  }

  constructor() {
    this.baseUrl = environment.baseUrlApi
    this.carregarI18n()
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.carregarI18n()
    })
    this.iniciarRefreshSeLogado()
    this.ouvirMudancasDeOutrasAbas()
  }

  executeLogin(username: string, password: string, onDone?: () => void): void {
    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)
    this.login(formData).subscribe({
      next: token => {
        if (token != '') {
          this.agendarRefresh()
          this.get().subscribe(usuario => {
            if (usuario) {
              const redirectConcursoId = this.storage.getRedirectConcursoId()
              if (redirectConcursoId != null) {
                this.storage.removeRedirectConcursoId()
                this.router.navigate([`/concurso/${redirectConcursoId}/inscricao`])
              } else {
                this.router.navigate(['/candidato/inscricoes'])
              }
            } else {
              this.toastService.showErrorToast(this.i18n.atencao, this.i18n.emailOuSenhaInvalido)
              onDone?.()
            }
          })
        } else {
          this.toastService.showErrorToast(this.i18n.atencao, this.i18n.emailOuSenhaInvalido)
          onDone?.()
        }
      },
      error: () => {
        this.toastService.showErrorToast(this.i18n.atencao, this.i18n.emailOuSenhaInvalido)
        onDone?.()
      },
    })
  }

  private login(form: FormData) {
    return this.http.post(`${this.baseUrl}/privado/login`, form, { responseType: 'text', withCredentials: true }).pipe(
      map(token => {
        if (token) {
          this.storage.setToken(token)
        } else {
          this.storage.removeToken()
        }
        return token
      })
    )
  }

  refreshToken(): Observable<string> {
    return this.http
      .post(`${this.baseUrl}/privado/candidato/refresh`, null, {
        responseType: 'text',
        withCredentials: true,
      })
      .pipe(
        map(token => {
          this.storage.setToken(token)
          this.agendarRefresh()
          return token
        })
      )
  }

  logout() {
    this.limparRefreshTimer()
    this.storage.clearSession()
    this.router.navigate(['/sistema/entrar'])
  }

  timeout() {
    this.limparRefreshTimer()
    this.toastService.showWarningToast(this.i18n.atencao, this.i18n.sessaoExpirada)
    this.storage.clearSession()
    this.router.navigate(['/sistema/entrar'])
  }

  get(): Observable<Usuario> {
    return this.http.get<Usuario>(`${this.baseUrl}/privado/candidato`).pipe(
      map(usuario => {
        this.storage.setEmail(usuario.email)
        this.storage.setId(usuario.id.toString())
        return usuario
      })
    )
  }

  isLoggedIn() {
    return this.storage.getToken() !== null
  }

  getCandidatoId(): number {
    const id = this.storage.getId()
    return id ? parseInt(id, 10) : 0
  }

  senhaRecuperar(email: string) {
    return this.http.get(`${this.baseUrl}/publico/senha-recuperar/${email}`, { responseType: 'text' })
  }

  senhaAlterar(jsonData: { senhaAtual: string | null; novaSenha: string | null }) {
    return this.http.post(`${this.baseUrl}/privado/candidato/senha-alterar`, jsonData, { responseType: 'text' })
  }

  senhaRedefinir(jsonData: { codigo: string; senha: string | null }) {
    return this.http.post(`${this.baseUrl}/publico/senha-redefinir`, jsonData, { responseType: 'text' })
  }

  emailAlterar(jsonData: { senhaAtual: string | null; novoEmail: string }) {
    return this.http.post(`${this.baseUrl}/privado/candidato/email-alterar`, jsonData, { responseType: 'text' })
  }

  emailConfirmar(token: string, senha: string) {
    return this.http.post(`${this.baseUrl}/publico/email-confirmar/${token}`, { senha }, { responseType: 'text' })
  }

  private iniciarRefreshSeLogado(): void {
    if (this.storage.getToken()) {
      this.agendarRefresh()
    }
  }

  private agendarRefresh(): void {
    this.limparRefreshTimer()
    const token = this.storage.getToken()
    if (!token) return

    try {
      const exp = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp
      const msAteExpirar = exp * 1000 - Date.now() - UsuarioService.REFRESH_MARGIN_MS
      if (msAteExpirar <= 0) return

      this.refreshSub = timer(msAteExpirar).subscribe(() => {
        this.refreshToken().subscribe({
          error: () => this.agendarRefresh(),
        })
      })
    } catch {
      // Token malformado — guard vai tratar
    }
  }

  private ouvirMudancasDeOutrasAbas(): void {
    fromEvent<StorageEvent>(this.window, 'storage')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(e => {
        if (e.key !== 'sinsc-candidato-token') return
        if (e.newValue) {
          this.agendarRefresh()
        } else {
          this.limparRefreshTimer()
        }
      })
  }

  private limparRefreshTimer(): void {
    this.refreshSub?.unsubscribe()
    this.refreshSub = undefined
  }

  private carregarI18n(): void {
    this.translateService
      .get([
        'shared.atencao',
        'usuario.seja_bem_vindo',
        'usuario.identificado_com_sucesso',
        'usuario.email_ou_senha_invalido',
        'usuario.acesso_finalizado',
        'usuario.sessao_expirada',
      ])
      .subscribe(traducoes => {
        this.i18n.atencao = traducoes['shared.atencao']
        this.i18n.sejaBemVindo = traducoes['usuario.seja_bem_vindo']
        this.i18n.identificadoComSucesso = traducoes['usuario.identificado_com_sucesso']
        this.i18n.emailOuSenhaInvalido = traducoes['usuario.email_ou_senha_invalido']
        this.i18n.acessoFinalizado = traducoes['usuario.acesso_finalizado']
        this.i18n.sessaoExpirada = traducoes['usuario.sessao_expirada']
      })
  }
}
